/**
 * OfficialMonthUpdatePage
 *
 * BUG-23: Admin UI for the Official Month Update (closed-month official score
 * restatement). Platform Admin only — lets a VeraScore admin apply the
 * member-facing OFFICIAL score for a CLOSED month from an approved restatement.
 *
 * Safety: this is a governed, fully-audited write. It requires an approved
 * restatement, is kill-switch gated server-side (SCORE_GOVERNANCE_WRITES_ENABLED),
 * and is only reversible via a formal reversal. A dry-run (default checked) and a
 * typed RESTATE confirmation guard the execute path. Server guard is
 * authoritative — the client gate is UX only, not security.
 *
 * Access gate (client mirror of server): 'Platform Admin' role only.
 */

import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FiCheckCircle, FiAlertTriangle, FiSearch, FiPower } from 'react-icons/fi';
import { useUser } from '../context/UserContext';

// ─── Platform API helper ─────────────────────────────────────────────────────
// The restatement endpoints live on api-verascore-platform (same origin as the
// admin member search), so they use the cookie session — no Bearer needed.
const API_BASE = import.meta.env.VITE_API_URL || '/api';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;

/** Strict UUIDv4 — mirrors the backend validator. */
const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** A member row as returned by GET /admin/members/search. */
interface MemberMatch {
  uuid: string;
  member_id: string | null;
  owner_id: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  lifecycle_status?: string | null;
}

interface RestatementApproval {
  approval_id: string;
  owner_id: string;
  member_id: string;
  year_and_month: string;
  reason_code: string | null;
  reason_summary: string | null;
  cpto_doc_ref: string | null;
  requested_by_user_id: string | null;
  approved_by_user_id: string | null;
  expires_at: string | null;
  status: string;
}

interface ComputeRun {
  run_id: string;
  score: number | null;
  rating: string | null;
  algorithm_version: string | null;
  created_at: string | null;
}

interface RestateResult {
  dryRun?: boolean;
  wouldCallRpc?: boolean;
  rpcName?: string;
  currentOfficial?: { score: number | null; rating: string | null } | null;
  proposed?: { score: number | null; rating: string | null } | null;
  status?: 'EXECUTED' | 'ALREADY_EXECUTED';
  runId?: string;
  auditEventId?: string | null;
  historyEventId?: string | null;
  approvalId?: string;
  note?: string | null;
}

/** Unwrap the platform ApiResponseHandler.success envelope: { status, data }. */
const unwrap = <T,>(json: any): T => (json?.data ?? json) as T;

/** Resolve a free-text identifier to member matches via the platform admin search. */
const searchMembers = async (q: string): Promise<MemberMatch[]> => {
  const res = await fetch(`${API_BASE}/verascore/admin/members/search?q=${encodeURIComponent(q)}`, {
    method: 'GET',
    headers: { 'VS-Client-Id': VS_CLIENT_ID, 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Member search failed (${res.status}): ${text}`);
  }
  const json = await res.json();
  const rows = Array.isArray(json) ? json : (json?.data ?? json?.results ?? []);
  return rows as MemberMatch[];
};

const fetchApprovals = async (ownerId: string, month: string): Promise<RestatementApproval[]> => {
  const res = await fetch(
    `${API_BASE}/verascore/admin/score/restate/approvals?ownerId=${encodeURIComponent(ownerId)}&month=${encodeURIComponent(month)}`,
    {
      method: 'GET',
      headers: { 'VS-Client-Id': VS_CLIENT_ID, 'Content-Type': 'application/json' },
      credentials: 'include',
    }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Approvals fetch failed (${res.status}): ${text}`);
  }
  const rows = unwrap<RestatementApproval[]>(await res.json());
  return Array.isArray(rows) ? rows : [];
};

const fetchComputeRuns = async (ownerId: string, month: string): Promise<ComputeRun[]> => {
  const res = await fetch(
    `${API_BASE}/verascore/admin/score/restate/compute-runs?ownerId=${encodeURIComponent(ownerId)}&month=${encodeURIComponent(month)}`,
    {
      method: 'GET',
      headers: { 'VS-Client-Id': VS_CLIENT_ID, 'Content-Type': 'application/json' },
      credentials: 'include',
    }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Compute-runs fetch failed (${res.status}): ${text}`);
  }
  const rows = unwrap<ComputeRun[]>(await res.json());
  return Array.isArray(rows) ? rows : [];
};

const callRestate = async (body: Record<string, unknown>): Promise<RestateResult> => {
  const res = await fetch(`${API_BASE}/verascore/admin/score/restate`, {
    method: 'POST',
    headers: { 'VS-Client-Id': VS_CLIENT_ID, 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${res.status}: ${text}`);
  }
  return unwrap<RestateResult>(await res.json());
};

// BUG-25: read/toggle the governed-writes kill switch (DB feature flag).
const fetchGovernanceFlag = async (): Promise<boolean> => {
  const res = await fetch(`${API_BASE}/verascore/admin/score/governance-flag`, {
    method: 'GET',
    headers: { 'VS-Client-Id': VS_CLIENT_ID, 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Governance flag fetch failed (${res.status}): ${text}`);
  }
  return unwrap<{ enabled: boolean }>(await res.json())?.enabled === true;
};

const setGovernanceFlag = async (enabled: boolean): Promise<boolean> => {
  const res = await fetch(`${API_BASE}/verascore/admin/score/governance-flag`, {
    method: 'POST',
    headers: { 'VS-Client-Id': VS_CLIENT_ID, 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ enabled }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Governance flag update failed (${res.status}): ${text}`);
  }
  return unwrap<{ enabled: boolean }>(await res.json())?.enabled === true;
};

const memberLabel = (m: MemberMatch): string => {
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ').trim();
  return [name || null, m.email || null, m.member_id || null]
    .filter(Boolean)
    .join(' • ') || m.uuid;
};

// ─── Month options (current month back 24 months) ────────────────────────────
function monthOptions(): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 25; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const base = d.toLocaleString('default', { month: 'long', year: 'numeric' });
    const label = i === 0 ? `${base} (current — open)` : base;
    out.push({ value, label });
  }
  return out;
}

const inputClass =
  'w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500';

const approvalLabel = (a: RestatementApproval): string => {
  const parts = [
    a.reason_code || 'restatement',
    a.approved_by_user_id ? `approved by ${a.approved_by_user_id}` : null,
    a.expires_at ? `expires ${a.expires_at}` : 'no expiry',
  ].filter(Boolean);
  return parts.join(' • ');
};

const runLabel = (r: ComputeRun): string => {
  const scorePart = r.score != null ? `${r.score}${r.rating ? ` (${r.rating})` : ''}` : 'no score';
  const shortRun = r.run_id.slice(0, 8);
  const created = r.created_at ? new Date(r.created_at).toLocaleString() : '';
  return `${scorePart} • ${shortRun} • ${created}`;
};

const OfficialMonthUpdatePage: FC = () => {
  const { activePersona } = useUser();
  const months = useMemo(monthOptions, []);

  // Member resolver state (mirrors IntraMonthTriggerPage).
  const [identifier, setIdentifier] = useState('');
  const [resolvedMember, setResolvedMember] = useState<MemberMatch | null>(null);
  const [matches, setMatches] = useState<MemberMatch[]>([]);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const resolveSeq = useRef(0);

  const [targetMonth, setTargetMonth] = useState(months[1]?.value ?? months[0]?.value ?? '');

  // Approval + compute-run selection.
  const [approvals, setApprovals] = useState<RestatementApproval[]>([]);
  const [computeRuns, setComputeRuns] = useState<ComputeRun[]>([]);
  const [selectedApprovalId, setSelectedApprovalId] = useState('');
  const [selectedRunId, setSelectedRunId] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const fetchSeq = useRef(0);
  const restateSeq = useRef(0);

  // Execute state.
  const [dryRun, setDryRun] = useState(true);
  const [confirmation, setConfirmation] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RestateResult | null>(null);
  const [executed, setExecuted] = useState(false);

  // BUG-25: governed-writes kill switch (DB flag) state.
  const [writesEnabled, setWritesEnabled] = useState<boolean | null>(null);
  const [flagBusy, setFlagBusy] = useState(false);
  const [flagError, setFlagError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    fetchGovernanceFlag()
      .then((v) => { if (active) setWritesEnabled(v); })
      .catch((err: any) => { if (active) setFlagError(err.message); });
    return () => { active = false; };
  }, []);

  const handleToggleWrites = async (next: boolean) => {
    setFlagBusy(true);
    setFlagError(null);
    try {
      const v = await setGovernanceFlag(next);
      setWritesEnabled(v);
    } catch (err: any) {
      setFlagError(err.message);
    } finally {
      setFlagBusy(false);
    }
  };

  const selectedApproval = approvals.find((a) => a.approval_id === selectedApprovalId) || null;
  const selectedRun = computeRuns.find((r) => r.run_id === selectedRunId) || null;

  const handleSetIdentifier = (v: string) => {
    resolveSeq.current += 1;
    setIdentifier(v);
    setResolvedMember(null);
    setMatches([]);
    setResolveError(null);
    resetOptions();
  };

  const resetOptions = () => {
    fetchSeq.current += 1;
    restateSeq.current += 1;
    setApprovals([]);
    setComputeRuns([]);
    setSelectedApprovalId('');
    setSelectedRunId('');
    setOptionsError(null);
    setOptionsLoaded(false);
    setResult(null);
    setError(null);
    setExecuted(false);
    setIdempotencyKey(null);
    setConfirmation('');
  };

  const loadOptions = async (ownerId: string | null, month: string) => {
    const seq = ++fetchSeq.current;
    setLoadingOptions(true);
    setOptionsError(null);
    setOptionsLoaded(false);
    setApprovals([]);
    setComputeRuns([]);
    setSelectedApprovalId('');
    setSelectedRunId('');
    if (!ownerId) {
      setOptionsError('Could not determine this member\u2019s Auth0 owner id (needed to look up approvals and compute runs). Resolve the member by email, member ID, or Auth0 ID rather than by raw UUID.');
      setLoadingOptions(false);
      setOptionsLoaded(true);
      return;
    }
    try {
      const [ap, runs] = await Promise.all([
        fetchApprovals(ownerId, month),
        fetchComputeRuns(ownerId, month),
      ]);
      if (seq !== fetchSeq.current) return;
      setApprovals(ap);
      setComputeRuns(runs);
      if (ap.length === 1) setSelectedApprovalId(ap[0].approval_id);
      if (runs.length === 1) setSelectedRunId(runs[0].run_id);
    } catch (err: any) {
      if (seq !== fetchSeq.current) return;
      setOptionsError(err.message);
    } finally {
      if (seq === fetchSeq.current) {
        setLoadingOptions(false);
        setOptionsLoaded(true);
      }
    }
  };

  const handleResolve = async () => {
    const q = identifier.trim();
    if (!q) return;
    const seq = ++resolveSeq.current;
    setResolving(true);
    setResolveError(null);
    setResolvedMember(null);
    setMatches([]);
    resetOptions();
    try {
      if (UUID_V4.test(q)) {
        if (seq !== resolveSeq.current) return;
        const m: MemberMatch = { uuid: q, member_id: null, owner_id: null, email: null, first_name: null, last_name: null };
        setResolvedMember(m);
        if (targetMonth) void loadOptions(m.owner_id, targetMonth);
        return;
      }
      const found = await searchMembers(q);
      if (seq !== resolveSeq.current) return;
      if (found.length === 0) {
        setResolveError('No member matched that identifier. Note: name and Auth0 ID lookup are not yet supported server-side.');
      } else if (found.length === 1) {
        setResolvedMember(found[0]);
        if (targetMonth) void loadOptions(found[0].owner_id, targetMonth);
      } else {
        setMatches(found);
      }
    } catch (err: any) {
      if (seq !== resolveSeq.current) return;
      setResolveError(err.message);
    } finally {
      if (seq === resolveSeq.current) setResolving(false);
    }
  };

  const handlePick = (m: MemberMatch) => {
    resolveSeq.current += 1;
    setResolvedMember(m);
    setMatches([]);
    resetOptions();
    if (targetMonth) void loadOptions(m.owner_id, targetMonth);
  };

  const handleSetMonth = (v: string) => {
    setTargetMonth(v);
    resetOptions();
    if (resolvedMember) void loadOptions(resolvedMember.owner_id, v);
  };

  const handleSelectApproval = (v: string) => {
    restateSeq.current += 1;
    setSelectedApprovalId(v);
    setResult(null);
    setExecuted(false);
  };

  const handleSelectRun = (v: string) => {
    restateSeq.current += 1;
    setSelectedRunId(v);
    setResult(null);
    setExecuted(false);
  };

  const handleDryRun = async () => {
    if (!selectedApproval || !selectedRun) return;
    restateSeq.current += 1;
    const seq = restateSeq.current;
    const key = crypto.randomUUID();
    setIdempotencyKey(key);
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await callRestate({
        approvalId: selectedApproval.approval_id,
        computeRunId: selectedRun.run_id,
        idempotencyKey: key,
        dryRun: true,
      });
      if (seq !== restateSeq.current) return;
      setResult(data);
    } catch (err: any) {
      if (seq !== restateSeq.current) return;
      setError(err.message);
    } finally {
      if (seq === restateSeq.current) setLoading(false);
    }
  };

  const handleExecute = async () => {
    if (!selectedApproval || !selectedRun || confirmation !== 'RESTATE') return;
    restateSeq.current += 1;
    const seq = restateSeq.current;
    const key = idempotencyKey ?? crypto.randomUUID();
    setIdempotencyKey(key);
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = await callRestate({
        approvalId: selectedApproval.approval_id,
        computeRunId: selectedRun.run_id,
        idempotencyKey: key,
        dryRun: false,
      });
      if (seq !== restateSeq.current) return;
      setResult(data);
      setExecuted(true);
    } catch (err: any) {
      if (seq !== restateSeq.current) return;
      setError(err.message);
    } finally {
      if (seq === restateSeq.current) setLoading(false);
    }
  };

  const currentOfficial = result?.currentOfficial ?? null;
  const proposedScore = selectedRun ? { score: selectedRun.score, rating: selectedRun.rating } : null;
  const canExecute =
    !!selectedApproval && !!selectedRun && confirmation === 'RESTATE' && !loading && !executed
    && writesEnabled === true;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <FiCheckCircle className="text-brand-blue" /> Official Month Update
        </h1>
        <p className="text-sm text-slate-500 mt-1">BUG-23 • Closed-month official restatement</p>
      </div>

      {/* AMBER/RED warning banner */}
      <div className="mb-6 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
        <FiAlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" />
        <div>
          This writes the member-facing <strong>OFFICIAL</strong> score for a closed month. It requires
          an approved restatement, is fully audited, and is only reversible via a formal reversal.
        </div>
      </div>

      {/* Form */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Member</label>
          <div className="flex gap-2">
            <input
              className={inputClass}
              placeholder="UUID, email, member ID, name, or auth0|…"
              value={identifier}
              onChange={(e) => handleSetIdentifier(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleResolve(); } }}
            />
            <button
              type="button"
              onClick={() => void handleResolve()}
              disabled={resolving || identifier.trim().length === 0}
              className="flex items-center gap-1 shrink-0 px-3 py-2 bg-slate-100 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FiSearch size={14} /> {resolving ? 'Resolving…' : 'Resolve'}
            </button>
          </div>
          <p className="mt-1 text-xs text-slate-400">
            Enter a UUID to use directly, or search by email / member ID / name / Auth0 ID.
          </p>

          {resolveError && <p className="mt-1 text-xs text-red-500">{resolveError}</p>}

          {matches.length > 1 && !resolvedMember && (
            <ul className="mt-2 max-h-56 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100">
              {matches.map((m) => (
                <li key={m.uuid}>
                  <button
                    type="button"
                    onClick={() => handlePick(m)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                  >
                    <span className="font-medium text-slate-700">{memberLabel(m)}</span>
                    <span className="block font-mono text-[11px] text-slate-400">{m.uuid}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {resolvedMember && (
            <div className="mt-2 flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              <FiCheckCircle size={14} className="mt-0.5 shrink-0" />
              <div>
                <span className="font-medium">{memberLabel(resolvedMember)}</span>
                <span className="block font-mono text-[11px] text-green-700">{resolvedMember.uuid}</span>
              </div>
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Target month</label>
          <select className={inputClass} value={targetMonth} onChange={(e) => handleSetMonth(e.target.value)}>
            {months.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        {/* Approval + compute-run selection (auto-fetched once member + month chosen) */}
        {resolvedMember && (
          <>
            {loadingOptions && (
              <p className="text-xs text-slate-400">Loading approvals and compute runs…</p>
            )}
            {optionsError && <p className="text-xs text-red-500">{optionsError}</p>}

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Approved restatement</label>
              {optionsLoaded && approvals.length === 0 ? (
                <p className="text-xs text-amber-700">No approved restatement found for this member+month.</p>
              ) : (
                <select
                  className={inputClass}
                  value={selectedApprovalId}
                  onChange={(e) => handleSelectApproval(e.target.value)}
                  disabled={approvals.length === 0}
                >
                  <option value="">Select an approval…</option>
                  {approvals.map((a) => (
                    <option key={a.approval_id} value={a.approval_id}>{approvalLabel(a)}</option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Compute run (restate from)</label>
              {optionsLoaded && computeRuns.length === 0 ? (
                <p className="text-xs text-amber-700">No SUCCEEDED intra-month compute run found — run an Intra-Month preview first.</p>
              ) : (
                <select
                  className={inputClass}
                  value={selectedRunId}
                  onChange={(e) => handleSelectRun(e.target.value)}
                  disabled={computeRuns.length === 0}
                >
                  <option value="">Select a compute run…</option>
                  {computeRuns.map((r) => (
                    <option key={r.run_id} value={r.run_id}>{runLabel(r)}</option>
                  ))}
                </select>
              )}
            </div>
          </>
        )}

        {/* Preview panel: current official → proposed */}
        {selectedRun && (
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1">
              <dt className="text-slate-500">Current official</dt>
              <dd className="font-semibold">
                {currentOfficial && currentOfficial.score != null
                  ? `${currentOfficial.score}${currentOfficial.rating ? ` (${currentOfficial.rating})` : ''}`
                  : '—'}
              </dd>
              <dt className="text-slate-500">Proposed</dt>
              <dd className="font-semibold">
                {proposedScore && proposedScore.score != null
                  ? `${proposedScore.score}${proposedScore.rating ? ` (${proposedScore.rating})` : ''}`
                  : '—'}
              </dd>
            </dl>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Admin actor</label>
          <input className={`${inputClass} bg-slate-50 text-slate-500`} value={activePersona.email} readOnly />
          <p className="mt-1 text-xs text-slate-400">Must match your authenticated email (server-enforced).</p>
        </div>

        <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-blue focus:ring-blue-500"
          />
          <span>
            <span className="font-medium text-slate-700">Dry-run first</span> — validate the restatement
            without writing. No RPC is called, no writes at all.
          </span>
        </label>

        {dryRun && (
          <button
            onClick={() => void handleDryRun()}
            disabled={!selectedApproval || !selectedRun || loading}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FiSearch size={14} /> {loading ? 'Checking…' : 'Dry-run'}
          </button>
        )}

        {/* BUG-25: Governed-writes kill switch (runtime toggle, no redeploy) */}
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={writesEnabled === true}
              disabled={flagBusy || writesEnabled === null}
              onChange={(e) => void handleToggleWrites(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-blue focus:ring-blue-500"
            />
            <span>
              <span className="font-medium text-slate-700">Enable governed writes</span> — kill switch
              for the OFFICIAL write below. Applies globally; turn OFF again when done.
              {' '}
              <span className="inline-flex items-center gap-1 font-semibold">
                <FiPower size={12} className={writesEnabled === true ? 'text-green-600' : 'text-slate-400'} />
                {writesEnabled === null ? (flagError ? 'unknown' : 'checking…') : writesEnabled ? 'ON' : 'OFF'}
                {flagBusy ? ' (updating…)' : ''}
              </span>
            </span>
          </label>
          {flagError && <p className="mt-1 text-xs text-red-500 break-all">{flagError}</p>}
        </div>

        {/* Typed confirmation + idempotency key */}
        <div>
          <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Type RESTATE to confirm</label>
          <input
            className={inputClass}
            placeholder="RESTATE"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
          {idempotencyKey && (
            <p className="mt-1 text-xs text-slate-400 font-mono break-all">Idempotency key: {idempotencyKey}</p>
          )}
        </div>

        <button
          onClick={() => void handleExecute()}
          disabled={!canExecute}
          className="flex items-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <FiCheckCircle size={14} /> {executed ? 'Applied' : loading ? 'Applying…' : 'Apply Official Update'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <FiAlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span className="break-all">{error}</span>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-5">
          <div className="flex items-center gap-2 mb-3">
            <FiCheckCircle className="text-green-600" size={18} />
            <span className="font-semibold text-green-800">
              {result.dryRun ? 'Dry-run (no writes)' : 'Official update applied'}
            </span>
          </div>
          {result.dryRun ? (
            <pre className="text-xs bg-white/70 border border-green-100 rounded-md p-3 overflow-auto">
              {JSON.stringify(result, null, 2)}
            </pre>
          ) : (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
              {result.status && (<><dt className="text-slate-500">Status</dt><dd className="font-semibold">{result.status}</dd></>)}
              {result.runId && (<><dt className="text-slate-500">Run ID</dt><dd className="font-mono text-xs break-all">{result.runId}</dd></>)}
              {result.auditEventId && (<><dt className="text-slate-500">Audit event ID</dt><dd className="font-mono text-xs break-all">{result.auditEventId}</dd></>)}
              {result.historyEventId && (<><dt className="text-slate-500">History event ID</dt><dd className="font-mono text-xs break-all">{result.historyEventId}</dd></>)}
              {result.note && (<><dt className="text-slate-500">Note</dt><dd>{result.note}</dd></>)}
            </dl>
          )}
        </div>
      )}
    </div>
  );
};

export default OfficialMonthUpdatePage;
